import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function Login() {
  const { login, requestMobileOtp, verifyMobileOtp } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleCustom, setGoogleCustom] = useState({ enabled: false, client_id: "" });

  useEffect(() => {
    // Runtime probe so newly-added GOOGLE_CLIENT_ID/SECRET secrets show up
    // without a frontend rebuild.
    api.get("/auth/google/config").then(r => setGoogleCustom(r.data)).catch(() => {});
  }, []);

  const redirect = (u) => {
    const to = loc.state?.from?.pathname ||
      ((u.role === "admin" || u.role === "subadmin") ? "/admin" : u.role === "trainer" ? "/trainer" : "/learner");
    nav(to, { replace: true });
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      redirect(u);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };

  const sendOtp = async () => {
    if (!phone) return setErr("Enter mobile number");
    setLoading(true); setErr("");
    try {
      await requestMobileOtp(phone);
      setOtpSent(true);
      toast.success("OTP sent (demo: 123456)");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await verifyMobileOtp({ phone, otp });
      toast.success("Welcome");
      redirect(u);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };

  const googleLoginEmergent = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const googleLoginCustom = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: googleCustom.client_id,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      include_granted_scopes: "true",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 grid-bg">
      <Seo
        title="Sign In – DriveTerrain"
        description="Sign in to your DriveTerrain account to book driving lessons, message your instructor, or manage your teaching schedule."
        canonical="/login"
        robots="noindex, follow"
        twitterCard="summary"
      />
      <div className="w-full max-w-md bg-card border border-border p-8" data-testid="login-card">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Sign in</div>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground mt-2">Continue your driving journey.</p>

        <Tabs defaultValue="email" className="mt-6">
          <TabsList className="grid grid-cols-2 w-full rounded-sm">
            <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
            <TabsTrigger value="mobile" data-testid="tab-mobile">Mobile</TabsTrigger>
          </TabsList>
          <TabsContent value="email" className="mt-6">
            <form onSubmit={submitEmail} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="rounded-sm" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="rounded-sm" />
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </div>
              <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="login-submit">{loading ? "Signing in..." : "Sign in"}</Button>
            </form>
          </TabsContent>
          <TabsContent value="mobile" className="mt-6">
            <form onSubmit={verifyOtp} className="space-y-4">
              <div>
                <Label>Mobile Number</Label>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100" required data-testid="login-phone" className="rounded-sm" />
              </div>
              {!otpSent ? (
                <Button type="button" onClick={sendOtp} className="w-full rounded-sm" disabled={loading} data-testid="send-otp-btn">{loading ? "Sending..." : "Send OTP"}</Button>
              ) : (
                <>
                  <div>
                    <Label>Enter OTP (demo: 123456)</Label>
                    <Input value={otp} onChange={(e) => setOtp(e.target.value)} required maxLength={6} data-testid="login-otp" className="rounded-sm tracking-widest text-center" />
                  </div>
                  <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="verify-otp-btn">{loading ? "Verifying..." : "Verify & Sign in"}</Button>
                </>
              )}
            </form>
          </TabsContent>
        </Tabs>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
          <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
        </div>

        <Button type="button" variant="outline" className="w-full rounded-sm" onClick={googleCustom.enabled ? googleLoginCustom : googleLoginEmergent} data-testid="google-login-btn">
          Continue with Google
        </Button>
        {googleCustom.enabled && (
          <button
            type="button"
            onClick={googleLoginEmergent}
            className="mt-2 text-xs text-muted-foreground hover:text-primary underline underline-offset-2 w-full text-center"
            data-testid="google-login-emergent-fallback"
          >
            Trouble signing in? Use Emergent Google Sign-in instead
          </button>
        )}

        {err && <div className="mt-4 text-sm text-destructive" data-testid="login-error">{err}</div>}
        <div className="mt-6 text-sm text-muted-foreground">
          New here? <Link to="/register" className="text-primary hover:underline" data-testid="link-register">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
